import { useEffect, useMemo, useState } from 'react';
import { Card } from './Card';

// FastingWindowCard — Dashboard ring showing the current eating/fasting state,
// a large countdown to the next transition, and the two labeled endpoints of
// the active state (e.g. "STARTED 18:00 yesterday" / "OPENS 09:00 today").
//
// Visual reference language: Zero / Fastic / Life Fasting all use a central
// progress ring with a numeric countdown and labeled start/end points. We
// keep that pattern in the project's paper system: a grey inset track with a
// single functional-colour arc.
//
// Same-day eating windows only (start < end). The server rejects wrap-midnight
// inputs; this UI mirrors that validation before POSTing.
export function FastingWindowCard({
  start,
  end,
  onSave,
}: {
  start: string; // 'HH:MM'
  end: string;   // 'HH:MM'
  onSave: (start: string, end: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draftStart, setDraftStart] = useState(start);
  const [draftEnd, setDraftEnd] = useState(end);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tick once per minute so the countdown + ring stay current. Not animation —
  // the granularity is HH:MM, so per-minute is correct.
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    setDraftStart(start);
    setDraftEnd(end);
  }, [start, end]);

  const state = useMemo(() => deriveState(now, start, end), [now, start, end]);

  const handleSave = async () => {
    setError(null);
    const sMin = hmToMinutes(draftStart);
    const eMin = hmToMinutes(draftEnd);
    if (sMin === null || eMin === null) {
      setError('Times must be HH:MM');
      return;
    }
    if (eMin <= sMin) {
      setError('End must be after start (same-day only)');
      return;
    }
    setSaving(true);
    try {
      await onSave(draftStart, draftEnd);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const action = !editing && (
    <button
      type="button"
      onClick={() => {
        setError(null);
        setEditing(true);
      }}
      className="btn-secondary text-[11px] px-3.5 py-2 font-mono uppercase tracking-[0.14em]"
      aria-label="Edit eating window"
    >
      Edit
    </button>
  );

  return (
    <Card eyebrow={state.label} action={action}>
      {!editing ? (
        <div className="space-y-5">
          <div className="flex justify-center pt-1">
            <FastingRing
              progress={state.progress}
              centerTime={state.centerTime}
              centerSub={state.centerSub}
              fasting={state.fasting}
            />
          </div>
          <EndpointPair state={state} />
          <p className="text-xs text-fg-faint leading-relaxed pt-1 border-t border-line">
            {state.fasting
              ? 'Fast in progress. Water, salt, black coffee, plain tea — no calories.'
              : 'Eating window open. Hit Meal 1 then Meal 2; last food by the close time.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1.5">
              <span className="label-eyebrow block">Start</span>
              <input
                type="time"
                value={draftStart}
                onChange={(e) => setDraftStart(e.target.value)}
                className="w-full rounded-control bg-bg-sunken border border-line px-3 py-2.5 font-mono text-sm tabular-nums text-fg transition-colors focus:outline-none focus:border-accent focus:bg-bg-raised"
              />
            </label>
            <label className="space-y-1.5">
              <span className="label-eyebrow block">End</span>
              <input
                type="time"
                value={draftEnd}
                onChange={(e) => setDraftEnd(e.target.value)}
                className="w-full rounded-control bg-bg-sunken border border-line px-3 py-2.5 font-mono text-sm tabular-nums text-fg transition-colors focus:outline-none focus:border-accent focus:bg-bg-raised"
              />
            </label>
          </div>
          {error && <p className="text-xs text-danger">{error}</p>}
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="btn-primary px-4 py-2 text-xs font-mono uppercase tracking-[0.14em] disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => {
                setDraftStart(start);
                setDraftEnd(end);
                setError(null);
                setEditing(false);
              }}
              disabled={saving}
              className="btn-secondary px-4 py-2 text-xs font-mono uppercase tracking-[0.14em]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

// FastingRing draws the SVG progress ring. The track is the sunken grey well;
// the arc and the NOW dot carry the one colour on the card. Hue does the work
// the old opacity dim used to do: green while the window is open (go), violet
// while fasting (the "not yet" mark of the system). Both are `-mid` steps, so
// the arc stays perceivable against the track instead of fading out.
function FastingRing({
  progress,
  centerTime,
  centerSub,
  fasting,
}: {
  progress: number;
  centerTime: string;
  centerSub: string;
  fasting: boolean;
}) {
  const size = 220;
  const cx = size / 2;
  const cy = size / 2;
  const r = 90;
  const circumference = 2 * Math.PI * r;
  const clamped = Math.min(1, Math.max(0, progress));
  const offset = circumference * (1 - clamped);
  // -90° rotation puts angle=0 at the top; angle then sweeps clockwise.
  const angleDeg = clamped * 360 - 90;
  const angleRad = (angleDeg * Math.PI) / 180;
  const dotX = cx + r * Math.cos(angleRad);
  const dotY = cy + r * Math.sin(angleRad);
  const arcColor = fasting ? 'var(--color-pop-mid)' : 'var(--color-good-mid)';

  // Fill the arc on mount: start empty (offset = full circumference), then
  // transition to the real offset one frame later. Reduced-motion users get the
  // snap via the global transition-duration guard.
  const [filled, setFilled] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setFilled(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={`${centerSub} ${centerTime}`}
    >
      {/* Background track — the sunken well, same grey as every other inset */}
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="currentColor"
        className="text-bg-sunken"
        strokeWidth={8}
      />
      {/* Tick marks at 12 / 3 / 6 / 9 o'clock — subtle hour reference */}
      {[0, 90, 180, 270].map((deg) => {
        const a = ((deg - 90) * Math.PI) / 180;
        const tickInner = r - 5;
        const tickOuter = r + 5;
        return (
          <line
            key={deg}
            x1={cx + tickInner * Math.cos(a)}
            y1={cy + tickInner * Math.sin(a)}
            x2={cx + tickOuter * Math.cos(a)}
            y2={cy + tickOuter * Math.sin(a)}
            stroke="currentColor"
            className="text-line-strong"
            strokeWidth={1}
          />
        );
      })}
      {/* Progress arc */}
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke={arcColor}
        strokeWidth={8}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={filled ? offset : circumference}
        transform={`rotate(-90 ${cx} ${cy})`}
        style={{ transition: 'stroke-dashoffset 1100ms cubic-bezier(0.65, 0, 0.35, 1)' }}
      />
      {/* NOW dot — only render when there's measurable progress so it doesn't
          sit at the 12 o'clock origin pretending to be a tick mark. Ringed in
          the card surface so it reads as a bead sitting on the arc. */}
      {clamped > 0.01 && clamped < 0.99 && (
        <circle
          cx={dotX}
          cy={dotY}
          r={6}
          fill={arcColor}
          stroke="var(--color-bg-raised, #ffffff)"
          strokeWidth={3}
        />
      )}
      {/* Center stack — countdown + subtitle. Anchored on the y baseline so
          larger countdowns ("13h 57m") align with the smaller one ("now"). */}
      <text
        x={cx}
        y={cy + 2}
        textAnchor="middle"
        dominantBaseline="middle"
        className="fill-fg font-display"
        style={{ fontSize: 36 }}
      >
        {centerTime}
      </text>
      <text
        x={cx}
        y={cy + 32}
        textAnchor="middle"
        dominantBaseline="middle"
        className="fill-fg-faint font-mono uppercase"
        style={{ fontSize: 10, letterSpacing: '0.18em' }}
      >
        {centerSub}
      </text>
    </svg>
  );
}

function EndpointPair({ state }: { state: DerivedState }) {
  return (
    <div className="flex items-center gap-2">
      <Endpoint label={state.startLabel} time={state.startTime} dayHint={state.startDayHint} active={false} />
      <span aria-hidden className="font-mono text-fg-faint text-lg">→</span>
      <Endpoint label={state.endLabel} time={state.endTime} dayHint={state.endDayHint} active />
    </div>
  );
}

function Endpoint({
  label,
  time,
  dayHint,
  active,
}: {
  label: string;
  time: string;
  dayHint: string | null;
  active: boolean;
}) {
  // The upcoming endpoint is the selected one, so it takes the ink outline —
  // the same black that marks every active state in the app.
  return (
    <div
      className={`flex-1 rounded-panel border bg-bg-sunken px-3 py-2 ${
        active ? 'border-accent' : 'border-line'
      }`}
    >
      <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-fg-faint leading-none">
        {label}
      </p>
      <p className={`font-display tabular-nums text-2xl leading-tight mt-1 ${active ? 'text-fg' : 'text-fg-muted'}`}>
        {time}
      </p>
      {dayHint && (
        <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-fg-faint leading-none mt-0.5">
          {dayHint}
        </p>
      )}
    </div>
  );
}

export type DerivedState = {
  fasting: boolean;
  label: string;            // eyebrow text: 'Fasting' | 'Eating window'
  centerTime: string;       // countdown like '13h 57m' or 'now'
  centerSub: string;        // small subtitle under the countdown
  startLabel: string;       // 'Started' / 'Opened'
  startTime: string;        // 'HH:MM'
  startDayHint: string | null;
  endLabel: string;         // 'Opens' / 'Closes'
  endTime: string;
  endDayHint: string | null;
  progress: number;         // 0..1, fraction of the current state elapsed
};

// deriveState computes the eating/fasting countdown + ring fraction at `now`.
// start/end are local HH:MM; `now` is a Date in the user's browser timezone.
//
// Same-day windows only. Malformed/wrap-midnight inputs return a neutral
// "Eating window" display with progress 0 so the dashboard doesn't NaN out.
export function deriveState(now: Date, start: string, end: string): DerivedState {
  const sMin = hmToMinutes(start);
  const eMin = hmToMinutes(end);
  if (sMin === null || eMin === null || eMin <= sMin) {
    return {
      fasting: false,
      label: 'Eating window',
      centerTime: '—',
      centerSub: 'Window unavailable',
      startLabel: 'Opens',
      startTime: start,
      startDayHint: null,
      endLabel: 'Closes',
      endTime: end,
      endDayHint: null,
      progress: 0,
    };
  }
  const cur = now.getHours() * 60 + now.getMinutes();
  const inEating = cur >= sMin && cur < eMin;
  const dayMin = 24 * 60;

  if (inEating) {
    const totalMins = eMin - sMin;
    const elapsedMins = cur - sMin;
    const remainingMins = totalMins - elapsedMins;
    return {
      fasting: false,
      label: 'Eating window',
      centerTime: formatRemaining(remainingMins),
      centerSub: 'until window closes',
      startLabel: 'Opened',
      startTime: start,
      startDayHint: 'today',
      endLabel: 'Closes',
      endTime: end,
      endDayHint: 'today',
      progress: totalMins > 0 ? elapsedMins / totalMins : 0,
    };
  }

  // Fasting branch — two sub-cases for whether the fast started yesterday or
  // today. Compute elapsed + total in minutes, then turn into ring progress.
  let elapsedMins: number;
  let startDayHint: string;
  let endDayHint: string;
  if (cur < sMin) {
    // Fast began yesterday at eMin; ends today at sMin.
    elapsedMins = dayMin - eMin + cur;
    startDayHint = 'yesterday';
    endDayHint = 'today';
  } else {
    // cur >= eMin — fast began today at eMin; ends tomorrow at sMin.
    elapsedMins = cur - eMin;
    startDayHint = 'today';
    endDayHint = 'tomorrow';
  }
  const totalMins = dayMin - (eMin - sMin); // length of the fast window
  const remainingMins = totalMins - elapsedMins;
  return {
    fasting: true,
    label: 'Fasting',
    centerTime: formatRemaining(remainingMins),
    centerSub: 'until window opens',
    startLabel: 'Started',
    startTime: end,
    startDayHint,
    endLabel: 'Opens',
    endTime: start,
    endDayHint,
    progress: totalMins > 0 ? elapsedMins / totalMins : 0,
  };
}

function hmToMinutes(hm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hm);
  if (!m) return null;
  const h = Number(m[1]);
  const mm = Number(m[2]);
  if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
  return h * 60 + mm;
}

function formatRemaining(totalMinutes: number): string {
  if (totalMinutes <= 0) return 'now';
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}
